import React, {Component, createRef} from 'react'
import propTypes from 'prop-types'
import {isEmpty} from 'lodash'
import iconNames from 'theme/icons'
import Translator from 'components/translator'
import Icon from 'components/icon'
import Timer from '../../helpers/timer'
import {prefix} from 'helpers/component'
import './searchBar.scss'

const getClassName = prefix('searchBar-')

const SEARCH_DELAY = 250
const Key = {
  esc: 27,
  enter: 13,
}

class SearchBar extends Component {
  constructor(props, context) {
    super(props, context)

    this.state = this.getDefaultState()
    this.inputRef = createRef()

    const searchDelay = this.props.searchDelay
      ? this.props.searchDelay
      : SEARCH_DELAY

    this.timer = new Timer(searchDelay, this.onSearch)
  }

  componentDidUpdate(prevProps) {
    const {value} = this.props
    if (value !== prevProps.value)
      this.setState({value: value || ''})
  }

  getDefaultState = () => {
    return {
      value: this.props.value || '',
      focussed: false,
    }
  }

  onChange = (e) => {
    this.setState({
      value: e.target.value,
    }, () => {
      this.timer.fire()
    })
  }

  onKeyDown = (e) => {
    const {onEnterPressed} = this.props

    switch (e.keyCode) {
      case Key.enter:  // Enter - search immediately
        this.timer.trigger()
        onEnterPressed && onEnterPressed()
        break

      case Key.esc:  // ESC - cancel search
        this.onClear()
        e.target.blur()
        break
    }
  }

  focus = () => {
    this.inputRef.current.focus()
  }

  clear = () => {
    this.onClear()
  }

  onSearch = () => {
    const {onSearch} = this.props
    const {value} = this.state

    onSearch(value)
  }

  onClear = () => {
    const {onClearSearch} = this.props
    this.setState({value: ''}, () => {
      if (onClearSearch) onClearSearch()
      this.timer.trigger()
    })
  }

  onFocussed = (e) => {
    const {onFocus} = this.props

    this.setState({
      focussed: true,
    })

    onFocus && onFocus(e)
  }

  onBlurred = () => {
    this.setState({
      focussed: false,
    })
  }

  getSearchIcon = () => {
    const {showIcon} = this.props
    return showIcon
      ? <span className={getClassName('icon')} onClick={this.focus}>
            <Icon name={iconNames.search}/>
          </span>
      : null
  }

  getClearIcon = () => {
    const {value} = this.state

    return value.length > 0
      ? <div className={getClassName('close')} onClick={this.onClear}>
        <Icon name={iconNames.clear}/>
      </div>
      : null
  }

  getClassName = () => {
    const {focussed} = this.state
    return focussed
      ? getClassName(['container', 'container-focussed'])
      : getClassName('container')
  }

  getSearchBarClassName = () => {
    const {dark, transparent, showIcon, bordered, className} = this.props

    const classes = ['input']
    if (dark) classes.push('input-dark')
    if (transparent) classes.push('input-transparent')
    if (showIcon) classes.push('input-with-icon')
    if (bordered) classes.push('input-bordered')

    return isEmpty(className)
      ? getClassName(classes)
      : `${getClassName(classes)} ${className}`
  }

  getSearchBarStyle = () => {
    const {styles} = this.props
    return styles || {}
  }

  getSearchBar = () => {
    const {placeholder, autoFocus} = this.props
    const {value} = this.state

    return (
      <Translator text={{placeholder}} onRender={({placeholder}) =>
        <input
          autoComplete={'off'}
          aria-label={placeholder}
          className={this.getSearchBarClassName()}
          ref={this.inputRef}
          style={this.getSearchBarStyle()}
          onChange={this.onChange}
          onKeyDown={this.onKeyDown}
          name={'search-bar'}
          value={value}
          placeholder={placeholder}
          onFocus={this.onFocussed}
          onBlur={this.onBlurred}
          autoFocus={autoFocus}
        />}
      />
    )
  }

  render() {
    const {transparent} = this.props

    return (
      <div className={this.getClassName()}>
        {this.getSearchBar()}
        {this.getSearchIcon()}
        {transparent && <div className={getClassName('transparent-background')}/>}
        {this.getClearIcon()}
      </div>
    )
  }
}

SearchBar.propTypes = {
  value: propTypes.string,
  searchDelay: propTypes.number,
  placeholder: propTypes.string,
  showIcon: propTypes.bool,
  dark: propTypes.bool,
  transparent: propTypes.bool,
  bordered: propTypes.bool,
  autoFocus: propTypes.bool,
  styles: propTypes.object,
  onSearch: propTypes.func.isRequired,
  onFocus: propTypes.func,
  onClearSearch: propTypes.func,
  onEnterPressed: propTypes.func,
  className: propTypes.string,
}

SearchBar.defaultProps = {
  placeholder: 'Search',
  autoFocus: true,
  transparent: false,
  dark: false,
  bordered: false,
}

export default SearchBar
